/* prettier-ignore */
import { CertificateState, RIASRecordType, SubscriptionState, RIASRoles } from "~/lib/types";
import { padId, schemeData, Subscription } from "~/lib";
/* prettier-ignore */
import { updateDB, putItem,	getProfile,	getByUser,	RIASTABLE,	runQueryCommand, getCounter } from "~/_server/dynamoDB";
import { sendEmail, sendDevEmail } from "~/_server/server.email";
import { SEND_TO_ADMINS, SEND_TO_DEV } from "~/lib/constants";
import { createInvoice, getInvoiceUrl } from "~/_server/accounts";
import { getByRowTypeServer } from "~/_server/dynamoQueries";

const addYearSeconds = (seconds: number, years: number = 1) => {
	const date = new Date(seconds * 1000);
	date.setFullYear(date.getFullYear() + years);
	return Math.floor(date.getTime() / 1000);
};

export const paymentRecieved = async (invoiceId: string) => {
	"use server";
	console.log("Payment received for invoice", invoiceId);

	let emailTemplate = null,
		emailProps = {};

	const r = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiInvoiceId",
		KeyConditionExpression: "invoiceId = :id",
		ExpressionAttributeValues: { ":id": invoiceId },
	});

	console.log("Matched invoice:", r);

	if (r?.items?.[0]) {
		const item = r.items[0];
		const recordType = item?.recordType;

		switch (recordType) {
			case "CERTIFICATE":
				await updateDB(item.pk, item.sk, { status: CertificateState.PAID });
				emailTemplate = "certificate-paid";
				break;

			case "SUBSCRIPTION":
				let update: { status?: string; expires?: number; joined?: number } = {};

				switch (item.status) {
					case SubscriptionState.ACTIVE:
					case SubscriptionState.RENEWING:
						update.status = SubscriptionState.ACTIVE;
						update.expires = addYearSeconds(item.expires);
						break;

					case SubscriptionState.SUSPENDED:
					case SubscriptionState.ASSESSING:
					case SubscriptionState.PENDING_BODY:
					case SubscriptionState.PRACTICAL:
						update.expires = addYearSeconds(item.expires);
						break;

					case SubscriptionState.PENDING:
					case SubscriptionState.APPLYING:
						// They become a certifier now (OK to re-purpose SubscriptionState.ACTIVE - it's just a string)
						await updateDB(
							item.pk,
							padId(parseInt(item.pk.split("#")[1]), RIASRecordType.USER),
							{ role: RIASRoles.CERTIFIER, status: SubscriptionState.ACTIVE },
						);

						const now = Math.floor(Date.now() / 1000);
						update.status =
							item.scheme == "cpd-membership"
								? SubscriptionState.CPD
								: SubscriptionState.ASSESSING;
						update.expires = addYearSeconds(
							now,
							item.scheme == "cpd-membership" ? 20 : 1,
						); // CPD is 'inifinte', others are 1 years
						update.joined = now;

						emailTemplate = "subscription-complete";
						break;
				}

				if (Object.keys(update).length) {
					await updateDB(item.pk, item.sk, update);
				}

				if (emailTemplate) {
					try {
						const user = await getProfile(parseInt(item.pk.split("#")[1]));
						const scheme = schemeData(item.scheme);

						let invoiceLink = await getInvoiceUrl(invoiceId);

						if (!("error" in invoiceLink)) {
							emailProps.schemeName = scheme.name;

							await sendEmail({
								templateName: emailTemplate,
								props: {
									name: user.firstName,
									schemeName: scheme,
									invoiceLink: invoiceLink.onlineInvoiceUrl,
								},
								to: user.email,
								bcc: SEND_TO_DEV,
							});
						}
					} catch (err) {
						await sendDevEmail(
							"Error sending email after payment",
							JSON.stringify(err),
						);
					}
				}
				break;
		}
	} else {
		await sendDevEmail(
			"Payment received but no matching record",
			`Invoice ID: ${invoiceId}`,
		);
	}
};

export const verifyUser = async (userId: number) => {
	"use server";

	try {
		const user = await getProfile(userId);

		if (!user?.targetScheme) {
			console.log("Error verifying user: missing targetScheme", { userId });
			return { success: false, error: "This user has no target scheme set on their profile." };
		}

		// Set user record to certifier
		const updateResult = await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "payment-pending",
			role: user.targetScheme == "cpd" ? RIASRoles.CPD : RIASRoles.CERTIFIER,
		});

		if ((updateResult as any)?.success === false) {
			console.log("Error verifying user: status update failed", { userId, error: (updateResult as any).error });
			return { success: false, error: "Could not update the user's status. Please try again." };
		}

		const subscriptions = (await getByUser("SUBSCRIPTION", userId, -1).then(
			r => r.items ?? [],
		)) as Subscription[];

		if (subscriptions.length === 0) {
			const name = await generateSubscriptionName( user.targetScheme );
			
			console.log("Creating subscription for user", name);

			const newSub: Subscription = {
				pk: `USER#${userId}`,
				sk: padId(
					parseInt(name.split("-").pop() as string, 10),
					RIASRecordType.SUBSCRIPTION,
				),
				status: SubscriptionState.APPLYING,
				expires: 0,
				recordType: RIASRecordType.SUBSCRIPTION,
				name,
				scheme: user.targetScheme,
			};

			await putItem(newSub);

			subscriptions.push(newSub);
		}

		for (const sub of subscriptions) {
			if (
				sub.status == SubscriptionState.APPLYING ||
				sub.status == SubscriptionState.VALIDATING
			) {
				try {
					// Create an invoice for joining and the first year's subs
					const invoiceId = await createSubscriptionInvoice(
						sub.scheme,
						parseInt(sub.sk?.replace("SUBSCRIPTION#", "") as string, 10),
						userId,
						true,
						new Date().getFullYear(),
					);

					if (invoiceId) {
						// Set subscription to paying
						await updateDB(sub.pk as string, sub.sk as string, {
							expires: 0,
							status: SubscriptionState.PENDING,
							invoiceId,
						});

						const user = await getProfile(userId);
						const scheme = schemeData( sub.scheme );
						
						console.log("User verified, invoice created:", invoiceId, "for user:", user.name, "scheme:", scheme);

						// Send an email to the user with next steps (payment)
						await sendEmail({
							templateName: "application-verified",
							props: {
								user: { name: user.firstName },
								schemeName: scheme.name,
								login: "https://rias-regs.co.uk/login",
							},
							to: user.email,
							bcc: SEND_TO_ADMINS,
						});
					}
				} catch (err) {
					console.log("Error verifying user", err);
				}
			}
		}

		return { success: true };
	} catch (err) {
		console.log("Error verifying user", err);
		return { success: false, error: "An unexpected error occurred while verifying this user." };
	}
};

export const suspendUser = async (userId: number) => {
	"use server";

	try {
		const subscriptions = (await getByUser("SUBSCRIPTION", userId, -1).then(
			r => r.items ?? [],
		)) as Subscription[];

		for (const sub of subscriptions) {
			if (sub.status == SubscriptionState.ACTIVE) {
				await updateDB(sub.pk as string, sub.sk as string, {
					status: SubscriptionState.SUSPENDED,
				});
			}
		}

		await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "suspended",
			suspended: Date.now(),
		});

		return { success: true };
	} catch (err) {
		console.log("Error suspending user", err);
		return { success: false, error: "An unexpected error occurred while suspending this user." };
	}
};

export const reactivateUser = async (userId: number) => {
	"use server";

	try {
		const subscriptions = (await getByUser("SUBSCRIPTION", userId, -1).then(
			r => r.items ?? [],
		)) as Subscription[];

		for (const sub of subscriptions) {
			if (sub.status == SubscriptionState.SUSPENDED) {
				await updateDB(sub.pk as string, sub.sk as string, {
					status: SubscriptionState.ACTIVE,
				});
			}
		}

		await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "active",
			suspended: 0,
		});

		return { success: true };
	} catch (err) {
		console.log("Error reactivating user", err);
		return { success: false, error: "An unexpected error occurred while reactivating this user." };
	}
};

export const createSubscriptionInvoice = async (
	schemeSlug: string,
	riasId: number,
	userId: number,
	incJoiningFee: boolean = false,
	year: number = new Date().getFullYear(),
) => {
	try {
		const user = await getProfile(userId);
		const scheme = schemeData(schemeSlug);

		if (scheme) {
			const lineItems = [];
			const isCPD = scheme.name == "CPD Membership";

			const description = isCPD
				? "CPD Membership"
				: `Certification of Design (${scheme.name})`;

			if (incJoiningFee) {
				// Logic for non RIAS members CPD fee.
				const isRIAS = (user.memberships?.rias ?? "").trim().length > 3;
				const joiningFee =
					isCPD && isRIAS ? scheme.joiningFeeRIASMember : scheme.joiningFee;

				lineItems.push({
					description: `Joining Fee - ${description} (#${riasId})`,
					quantity: 1.0,
					unitAmount: joiningFee,
					accountCode: "203",
					lineAmount: joiningFee,
				});
			}

			if ((scheme.renewalFee ?? 0) > 0) {
				lineItems.push({
					description: `Subscription Fee (${year}) - ${description} (#${riasId})`,
					quantity: 1.0,
					unitAmount: scheme.renewalFee,
					accountCode: "201",
					lineAmount: scheme.renewalFee,
				});
			}

			const { invoiceId } = await createInvoice(
				user.email,
				`${user.firstName} ${user.lastName}`,
				lineItems,
				riasId,
			);

			return invoiceId;
		} else {
			throw new Error("Scheme not found: " + schemeSlug);
		}
	} catch (err) {
		console.log("Error creating subscription invoice", err);
	}

	return null;
};

export const matchABExpiry = async () => {
	"use server";

	const subs = await getByRowTypeServer(
		RIASRecordType.SUBSCRIPTION,
		2000,
		"#expires,#approvedBodyId",
		{ "#expires": "expires", "#approvedBodyId": "approvedBodyId" },
	);

	const abs = await getByRowTypeServer(
		RIASRecordType.APPROVEDBODY,
		2000,
		"#expires,#sk,#pk",
		{ "#expires": "expires", "#sk": "sk", "#pk": "pk" },
	);

	const byPk = new Map(
		abs?.items.map(item => [
			parseInt(item.pk.replace("APPROVEDBODY#", "")),
			item.expires,
		]) ?? [],
	);

	const result = subs.items.reduce((acc, item) => {
		const { approvedBodyId, expires } = item;

		if (
			expires &&
			approvedBodyId &&
			(!byPk.has(parseInt(approvedBodyId)) ||
				expires > byPk.get(parseInt(approvedBodyId)))
		) {
			if (!acc[approvedBodyId] || expires > acc[approvedBodyId]) {
				acc[approvedBodyId] = expires;
			}
		}
		return acc;
	}, {});

	for (const [approvedBodyId, expires] of Object.entries(result)) {
		const pk = `APPROVEDBODY#${approvedBodyId}`;
		const sk = `APPROVEDBODY#${approvedBodyId.toString().padStart(7, "0")}`;

		await updateDB(pk, sk, { expires });
	}

	return;
};

export const submitApprovedBody = async (
	subscription: Subscription,
	data: {
		address: Record<string, string>;
		coordinator: { name: string; email: string; telephone: string };
		deputy: { name: string; email: string; telephone: string };
	},
) => {
	"use server";

	const userId = parseInt( subscription.pk?.replace( "USER#", "" ) ?? "0" );

	await updateDB( subscription.pk as string, subscription.sk as string, {
		pendingAB: {
			address: { ...data.address, title: data.address.name },
			coordinator: data.coordinator,
			deputy: data.deputy,
			submitted: Math.floor( Date.now() / 1000 ),
		},
		status: SubscriptionState.PENDING_BODY,
	} );

	const user = await getProfile( userId );
	const scheme = schemeData( subscription.scheme );

	await sendEmail( {
		templateName: "approved-body-request",
		props: {
			user: { name: user?.name },
			schemeName: scheme?.name,
			applicationName: data.address.name,
			url: `https://rias-regs.co.uk/dashboard/admin/users/${userId}`,
		},
		to: SEND_TO_ADMINS,
	} );

	await sendEmail( {
		templateName: "approved-body-submitted",
		props: {
			user: { name: user?.firstName },
			schemeName: scheme?.name,
		},
		to: user?.email,
	} );
};

const generateSubscriptionName = async (schemeSlug: string) => {
	"use server";

	const scheme = schemeData(schemeSlug);

	if (!scheme) {
		throw new Error("Scheme not found: " + schemeSlug);
	}

	const subId = await getCounter("SUBSCRIPTION");

	if (subId < 1) {
		throw new Error("Error generating subscriptionId");
	}

	return `${scheme.prefix}-D-${ subId.toFixed(0).padStart(7, "0")}`;
};
